/**
 * Wizard Context
 *
 * State management for the evaluation wizard.
 * Handles step navigation, auto-save, and data persistence.
 */

import { createContext, useContext, useReducer, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useEvaluation, useSaveWizardState, useWizardState } from '../useEvaluations';
import type { TaskScore, CoreTaskScore, CompetencyScore, AgreementInput } from '../../../lib/evaluationsApi';
import type { AgreementWithDescription } from './AgreementTypeSelector';

// Types
export interface WizardStepData {
  step1?: {
    viewed: boolean;
  };
  step2?: {
    taskScores: TaskScore[];
    totalTasks?: number;  // Track total tasks to score
    taskAgreements?: Record<number, AgreementWithDescription[]>;  // Agreements per task
    // Epic 4 — Beoordelen op kerntaken (Story 4.2). Parallel aan taskScores
    // hierboven, gebruikt door StepKerntaken voor core_tasks_v2-beoordelingen
    // i.p.v. Step2Tasks. Beide bestaan naast elkaar (AD-4/AD-9) — nooit één
    // vervangen door de ander.
    coreTaskScores?: CoreTaskScore[];
    totalCoreTasks?: number;
  };
  step3?: {
    vakCompetencyScores: CompetencyScore[];
    totalVakCompetencies?: number;  // Track total vak competencies to score
    vakCompetencyAgreements?: Record<number, AgreementWithDescription[]>;  // Agreements per vak competency
  };
  step4?: {
    basisCompetencyScores: CompetencyScore[];
    totalBasisCompetencies?: number;  // Track total basis competencies to score
    basisCompetencyAgreements?: Record<number, AgreementWithDescription[]>;  // Agreements per basis competency
  };
  step5?: {
    reviewed: boolean;
  };
  step6?: {
    agreements: AgreementInput[];
    opmerkingen?: string; // General remarks from manager
  };
}

interface WizardState {
  evaluationId: number;
  currentStep: number;
  stepData: WizardStepData;
  isLoading: boolean;
  isSaving: boolean;
  isDirty: boolean;
  error: string | null;
}

type WizardAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_SAVING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'UPDATE_STEP_DATA'; payload: { step: keyof WizardStepData; data: unknown } }
  | { type: 'LOAD_STATE'; payload: { currentStep: number; stepData: WizardStepData } }
  | { type: 'MARK_CLEAN' }
  | { type: 'MARK_DIRTY' };

export interface IncompleteStepInfo {
  step: number;
  name: string;
  scored: number;
  total: number;
}

interface WizardContextValue extends WizardState {
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateStepData: <K extends keyof WizardStepData>(step: K, data: WizardStepData[K]) => void;
  saveProgress: () => Promise<void>;
  canNavigateToStep: (step: number) => boolean;
  isStepComplete: (step: number) => boolean;
  areAllScoresComplete: () => boolean;
  getIncompleteSteps: () => IncompleteStepInfo[];
}

const WizardContext = createContext<WizardContextValue | null>(null);

const TOTAL_STEPS = 6;

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_SAVING':
      return { ...state, isSaving: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_STEP':
      return { ...state, currentStep: Math.max(1, Math.min(TOTAL_STEPS, action.payload)) };
    case 'NEXT_STEP':
      return { ...state, currentStep: Math.min(TOTAL_STEPS, state.currentStep + 1) };
    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(1, state.currentStep - 1) };
    case 'UPDATE_STEP_DATA':
      return {
        ...state,
        stepData: { ...state.stepData, [action.payload.step]: action.payload.data },
        isDirty: true,
      };
    case 'LOAD_STATE':
      return {
        ...state,
        currentStep: action.payload.currentStep,
        stepData: action.payload.stepData,
        isLoading: false,
        isDirty: false,
      };
    case 'MARK_CLEAN':
      return { ...state, isDirty: false };
    case 'MARK_DIRTY':
      return { ...state, isDirty: true };
    default:
      return state;
  }
}

interface WizardProviderProps {
  evaluationId: number;
  children: ReactNode;
}

export function WizardProvider({ evaluationId, children }: WizardProviderProps) {
  const [state, dispatch] = useReducer(wizardReducer, {
    evaluationId,
    currentStep: 1,
    stepData: {},
    isLoading: true,
    isSaving: false,
    isDirty: false,
    error: null,
  });

  const { data: evaluation } = useEvaluation(evaluationId);
  const { data: wizardState, isLoading: isLoadingWizard } = useWizardState(evaluationId);
  const saveWizard = useSaveWizardState();
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load wizard state from server
  useEffect(() => {
    if (!isLoadingWizard && wizardState) {
      dispatch({
        type: 'LOAD_STATE',
        payload: {
          currentStep: wizardState.currentStep || 1,
          stepData: (wizardState.stepData as WizardStepData) || {},
        },
      });
    } else if (!isLoadingWizard) {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [isLoadingWizard, wizardState]);

  // Auto-save with debounce
  useEffect(() => {
    if (state.isDirty && !state.isSaving) {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        saveProgress();
      }, 2000);
    }
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [state.isDirty, state.stepData, state.currentStep]);

  const saveProgress = useCallback(async () => {
    if (state.isSaving) return;
    dispatch({ type: 'SET_SAVING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    try {
      await saveWizard.mutateAsync({
        evaluationId,
        currentStep: state.currentStep,
        stepData: state.stepData as Record<string, unknown>,
      });
      dispatch({ type: 'MARK_CLEAN' });
    } catch {
      dispatch({ type: 'SET_ERROR', payload: 'Fout bij opslaan. Probeer het opnieuw.' });
    } finally {
      dispatch({ type: 'SET_SAVING', payload: false });
    }
  }, [evaluationId, state.currentStep, state.stepData, state.isSaving, saveWizard]);

  const goToStep = useCallback((step: number) => {
    dispatch({ type: 'SET_STEP', payload: step });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: 'NEXT_STEP' });
  }, []);

  const prevStep = useCallback(() => {
    dispatch({ type: 'PREV_STEP' });
  }, []);

  const updateStepData = useCallback(<K extends keyof WizardStepData>(
    step: K,
    data: WizardStepData[K]
  ) => {
    dispatch({ type: 'UPDATE_STEP_DATA', payload: { step, data } });
  }, []);

  // Check if ALL scores are filled for steps 2-4 (required for step 5)
  const areAllScoresComplete = useCallback((): boolean => {
    const step2Data = state.stepData.step2;
    const step3Data = state.stepData.step3;
    const step4Data = state.stepData.step4;

    // Check step 2: all tasks scored (tasks_v1) OR all kerntaken scored
    // (core_tasks_v2, Epic 4 Story 4.2) — exactly one pair is ever populated
    // per evaluation, since Step2Tasks/StepKerntaken are mutually exclusive
    // (AD-4 wizard-dispatch).
    const totalTasks = (step2Data?.totalTasks ?? 0) + (step2Data?.totalCoreTasks ?? 0);
    const scoredTasks = (step2Data?.taskScores?.length ?? 0) + (step2Data?.coreTaskScores?.length ?? 0);
    const step2Complete = totalTasks === 0 || scoredTasks >= totalTasks;

    // Check step 3: all vak competencies scored
    const totalVakComps = step3Data?.totalVakCompetencies ?? 0;
    const scoredVakComps = step3Data?.vakCompetencyScores?.length ?? 0;
    const step3Complete = totalVakComps === 0 || scoredVakComps >= totalVakComps;

    // Check step 4: all basis competencies scored
    const totalBasisComps = step4Data?.totalBasisCompetencies ?? 0;
    const scoredBasisComps = step4Data?.basisCompetencyScores?.length ?? 0;
    const step4Complete = totalBasisComps === 0 || scoredBasisComps >= totalBasisComps;

    return step2Complete && step3Complete && step4Complete;
  }, [state.stepData]);

  // Get detailed info about which steps are incomplete
  const getIncompleteSteps = useCallback((): IncompleteStepInfo[] => {
    const incomplete: IncompleteStepInfo[] = [];
    const step2Data = state.stepData.step2;
    const step3Data = state.stepData.step3;
    const step4Data = state.stepData.step4;

    // Check step 2: all tasks scored (tasks_v1) OR all kerntaken scored (core_tasks_v2)
    const totalTasks = (step2Data?.totalTasks ?? 0) + (step2Data?.totalCoreTasks ?? 0);
    const scoredTasks = (step2Data?.taskScores?.length ?? 0) + (step2Data?.coreTaskScores?.length ?? 0);
    if (totalTasks > 0 && scoredTasks < totalTasks) {
      incomplete.push({
        step: 2,
        name: step2Data?.totalCoreTasks ? 'Kerntaken' : 'Taken',
        scored: scoredTasks,
        total: totalTasks,
      });
    }

    // Check step 3: all vak competencies scored
    const totalVakComps = step3Data?.totalVakCompetencies ?? 0;
    const scoredVakComps = step3Data?.vakCompetencyScores?.length ?? 0;
    if (totalVakComps > 0 && scoredVakComps < totalVakComps) {
      incomplete.push({
        step: 3,
        name: 'Vakcompetenties',
        scored: scoredVakComps,
        total: totalVakComps,
      });
    }

    // Check step 4: all basis competencies scored
    const totalBasisComps = step4Data?.totalBasisCompetencies ?? 0;
    const scoredBasisComps = step4Data?.basisCompetencyScores?.length ?? 0;
    if (totalBasisComps > 0 && scoredBasisComps < totalBasisComps) {
      incomplete.push({
        step: 4,
        name: 'Basiscompetenties',
        scored: scoredBasisComps,
        total: totalBasisComps,
      });
    }

    return incomplete;
  }, [state.stepData]);

  const isStepComplete = useCallback((step: number): boolean => {
    switch (step) {
      case 1:
        return state.stepData.step1?.viewed === true;
      case 2:
        // Step 2 is complete if visited (for free navigation)
        return state.stepData.step2 !== undefined;
      case 3:
        // Step 3 is complete if visited (for free navigation)
        return state.stepData.step3 !== undefined;
      case 4:
        // Step 4 is complete if visited (for free navigation)
        return state.stepData.step4 !== undefined;
      case 5:
        return state.stepData.step5?.reviewed === true;
      case 6:
        return evaluation?.status === 'submitted' || evaluation?.status === 'final';
      default:
        return false;
    }
  }, [state.stepData, evaluation]);

  const canNavigateToStep = useCallback((step: number): boolean => {
    // Steps 1-4: Free navigation (always allowed once step 1 is viewed)
    if (step <= 4) {
      // Step 1 always accessible
      if (step === 1) return true;
      // Steps 2-4 require step 1 to be viewed
      return state.stepData.step1?.viewed === true;
    }

    // Step 5 (Review): Only when ALL scores in steps 2-4 are complete
    if (step === 5) {
      return state.stepData.step1?.viewed === true && areAllScoresComplete();
    }

    // Step 6: Requires step 5 to be reviewed
    if (step === 6) {
      return state.stepData.step5?.reviewed === true;
    }

    return false;
  }, [state.stepData, areAllScoresComplete]);

  const value: WizardContextValue = {
    ...state,
    goToStep,
    nextStep,
    prevStep,
    updateStepData,
    saveProgress,
    canNavigateToStep,
    isStepComplete,
    areAllScoresComplete,
    getIncompleteSteps,
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
}

export function useWizard(): WizardContextValue {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
}
